/**
 * BaseCloudProvider — shared scaffolding for cloud provider implementations.
 *
 * Adapted from yaqatw's src/service/cloud/BaseCloudProvider.ts. qda is a
 * plain browser app (not a Word add-in), so the one thing that changes is
 * the auth-dialog transport: yaqatw uses Office.js's
 * `Office.context.ui.displayDialogAsync`; this uses a plain `window.open`
 * popup + `postMessage`, landing on `public/redirect.html` (the
 * browser-native sibling of yaqatw's own `public/redirect.html`, same
 * filename by design). Everything
 * else — throttling, the auth-blocked gate, bearer-token fetch with
 * 401/429 handling, localStorage token persistence — is unchanged.
 *
 * Concretely provides:
 * - request throttling (single-flight + min interval between requests)
 * - popup-based OAuth dialog plumbing (open, parse message, handle user-cancel)
 * - bearer-token fetch wrapper with 401 re-auth and 429 retry-with-backoff
 * - localStorage token persistence
 *
 * Subclasses override:
 * - tokenName (storage key) and providerName (error messages)
 * - getAuthUrl() — provider-specific OAuth authorize URL
 * - parseAuthMessage() — extracts token/code from the popup's postMessage (default: implicit-grant token)
 * - All Provider interface methods: pull/push/delete/getMetadata/getMetadataAll
 */

import Events from "./events";
import type { OAuthDialogMessage, FileMetadata, PushResult, UploadResult, SyncEntry } from "./types";
import { getSettingValue } from "../settings/settingsStore";
import { maxRetryAttempts } from "./retryPolicy";

export abstract class BaseCloudProvider {
  abstract readonly tokenName: string;
  protected abstract readonly providerName: string;

  // Extension-less on purpose: Cloudflare Pages serves public/redirect.html at
  // the clean URL /redirect (and 308-redirects /redirect.html -> /redirect), so
  // registering the clean form with each OAuth provider avoids the extra hop.
  // Local dev (react-scripts) doesn't do clean URLs, so src/setupProxy.js serves
  // redirect.html at /redirect there to match.
  protected readonly redirectUri = `${window.location.origin}/redirect`;

  public queue: Map<string, Promise<any>> = new Map();

  /** Request throttling state. Override MIN_REQUEST_INTERVAL_MS per provider. */
  protected requestQueue: Promise<void> = Promise.resolve();
  protected lastRequestTime: number = 0;
  protected readonly MIN_REQUEST_INTERVAL_MS: number = 400;

  /** Default fetch timeout per attempt. Settings > Backend > Advanced > Cloud request timeout. */
  protected get FETCH_TIMEOUT_MS(): number {
    const v = getSettingValue("backend", "cloudTimeoutSec");
    return (typeof v === "number" && v > 0 ? v : 15) * 1000;
  }

  /**
   * Auth gate: blocks repeated dialog re-opens after the user cancels OAuth or
   * after too many consecutive failures. Reset by Sync.syncNow() so the user's
   * explicit retry always gets a fresh chance.
   */
  private authBlocked: { reason: "cancelled" | "failed" } | null = null;
  private authFailCount = 0;
  private static readonly MAX_AUTH_FAILS = 2;

  public resetAuthGate(): void {
    this.authBlocked = null;
    this.authFailCount = 0;
  }

  // ── Throttle ────────────────────────────────────────────────────────────

  protected async throttle(): Promise<void> {
    const currentTask = this.requestQueue.then(async () => {
      const now = Date.now();
      const timeSinceLast = now - this.lastRequestTime;
      if (timeSinceLast < this.MIN_REQUEST_INTERVAL_MS) {
        await new Promise((r) => setTimeout(r, this.MIN_REQUEST_INTERVAL_MS - timeSinceLast));
      }
      this.lastRequestTime = Date.now();
    });
    this.requestQueue = currentTask.catch(() => {});
    return currentTask;
  }

  // ── OAuth popup ─────────────────────────────────────────────────────────

  /**
   * Subclasses produce the full provider-specific authorize URL.
   */
  protected abstract getAuthUrl(): string;

  /**
   * Parse the popup's postMessage. Default handles implicit-grant providers
   * (Dropbox/GDrive/OneDrive) that postMessage `{success, token}`. Nextcloud
   * overrides to handle `{success, code}` and exchange the code itself.
   */
  protected parseAuthMessage(message: OAuthDialogMessage): { token?: string; code?: string } {
    if (message.error) throw new Error(`Authentication failed: ${message.error}`);
    if (message.success && message.token) return { token: message.token };
    if (message.success && message.code) return { code: message.code };
    throw new Error("Auth dialog returned no token or code");
  }

  /**
   * Open `authUrl` in a centered popup, await its postMessage (sent by
   * public/redirect.html back to window.opener), and return whatever
   * parseAuthMessage extracts. Subclasses use this from authenticate().
   *
   * Mirrors yaqatw's Office.js dialog contract exactly: a user closing the
   * popup before completing auth rejects with a message containing
   * "was cancelled" — the same string authenticate()'s authBlocked gate
   * already matches on (see below), so that logic needed no changes here.
   */
  protected openAuthDialog(
    authUrl: string,
    dialogSize: { height: number; width: number } = { height: 500, width: 500 }
  ): Promise<{ token?: string; code?: string }> {
    return new Promise((resolve, reject) => {
      const left = window.screenX + Math.max(0, (window.outerWidth - dialogSize.width) / 2);
      const top = window.screenY + Math.max(0, (window.outerHeight - dialogSize.height) / 2);
      const popup = window.open(
        authUrl,
        "yaqadocs-oauth",
        `width=${dialogSize.width},height=${dialogSize.height},left=${left},top=${top}`
      );

      if (!popup) {
        reject(
          new Error(
            `Failed to open auth dialog: popup blocked. Allow popups for this site and try again.`
          )
        );
        return;
      }

      let settled = false;
      const cleanup = () => {
        window.removeEventListener("message", onMessage);
        clearInterval(poll);
      };

      const onMessage = (e: MessageEvent) => {
        if (e.source !== popup || e.origin !== window.location.origin) return;
        settled = true;
        cleanup();
        try {
          const parsed = this.parseAuthMessage(e.data as OAuthDialogMessage);
          popup.close();
          resolve(parsed);
        } catch (error) {
          popup.close();
          reject(error);
        }
      };
      window.addEventListener("message", onMessage);

      const poll = setInterval(() => {
        if (popup.closed && !settled) {
          cleanup();
          reject(new Error(`${this.providerName} authentication was cancelled.`));
        }
      }, 500);
    });
  }

  /**
   * Validate that the OAuth client id is configured. Subclasses call this at
   * the top of authenticate().
   */
  protected assertClientId(clientId: string | undefined, envVarName: string): void {
    if (!clientId || clientId === "undefined") {
      const error = `${this.providerName} Client ID is missing. Please configure ${envVarName} in your environment variables.`;
      console.error(error);
      Events.emit("showError", error);
      throw new Error(error);
    }
  }

  /**
   * Gate around performAuthenticate(): prevents re-opening the OAuth dialog
   * after the user cancelled, or after MAX_AUTH_FAILS consecutive failures.
   * Subclasses override performAuthenticate(), not this method.
   */
  async authenticate(): Promise<string> {
    if (this.authBlocked) {
      throw this.makeAuthBlockedError(this.authBlocked.reason);
    }
    try {
      const token = await this.performAuthenticate();
      this.authFailCount = 0;
      return token;
    } catch (err: any) {
      const msg = String(err?.message || err);
      if (msg.includes("was cancelled")) {
        this.authBlocked = { reason: "cancelled" };
        throw this.makeAuthBlockedError("cancelled", err);
      }
      this.authFailCount += 1;
      if (this.authFailCount >= BaseCloudProvider.MAX_AUTH_FAILS) {
        this.authBlocked = { reason: "failed" };
        throw this.makeAuthBlockedError("failed", err);
      }
      throw err;
    }
  }

  private makeAuthBlockedError(reason: "cancelled" | "failed", cause?: Error): Error {
    const e = new Error(
      `${this.providerName} sign-in is paused (${reason}). Click "Sync now" to try again.`
    );
    (e as any).authBlocked = true;
    if (cause) (e as any).cause = cause;
    return e;
  }

  static isAuthBlocked(err: unknown): boolean {
    return !!(err && typeof err === "object" && (err as any).authBlocked === true);
  }

  /**
   * Default implicit-grant flow — opens the popup, stores token in localStorage.
   * Providers using a different flow (Nextcloud, OneDrive, S3) override this.
   */
  protected async performAuthenticate(): Promise<string> {
    const result = await this.openAuthDialog(this.getAuthUrl());
    if (!result.token) throw new Error(`${this.providerName} authenticate(): no token returned`);
    localStorage.setItem(this.tokenName, result.token);
    return result.token;
  }

  // ── Token access ────────────────────────────────────────────────────────

  protected async getAccessToken(): Promise<string> {
    let token = localStorage.getItem(this.tokenName);
    if (!token) token = await this.authenticate();
    return token;
  }

  /**
   * Subclasses override if they need custom refresh logic (Nextcloud uses
   * refresh tokens). Default re-authenticates the user.
   */
  protected async refreshAccessToken(): Promise<string> {
    localStorage.removeItem(this.tokenName);
    return this.authenticate();
  }

  // ── Authenticated fetch ─────────────────────────────────────────────────

  /**
   * Fetch with bearer token, 401 → refresh-and-retry, 429 → backoff retry.
   * Subclasses may override (e.g. GDrive needs network-error retry).
   */
  protected async fetchWithAuth(
    url: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<Response> {
    await this.throttle();

    let token = await this.getAccessToken();
    options.headers = { ...options.headers, Authorization: `Bearer ${token}` };

    let response = await fetch(url, {
      ...options,
      signal: AbortSignal.timeout(this.FETCH_TIMEOUT_MS),
    });

    if (response.status === 401) {
      console.warn(`[${this.providerName}] Received 401, refreshing token...`);
      token = await this.refreshAccessToken();
      options.headers = { ...options.headers, Authorization: `Bearer ${token}` };
      response = await fetch(url, {
        ...options,
        signal: AbortSignal.timeout(this.FETCH_TIMEOUT_MS),
      });
    }

    if (response.status === 429) {
      if (retryCount > maxRetryAttempts()) {
        throw new Error(`${this.providerName}: rate limit exceeded and max retries reached.`);
      }
      const retryAfter = response.headers.get("Retry-After");
      const waitTime = retryAfter
        ? parseInt(retryAfter, 10) * 1000
        : 2000 * Math.pow(2, retryCount);
      console.warn(`[${this.providerName}] Rate limited (429). Retrying in ${waitTime}ms...`);
      await new Promise((r) => setTimeout(r, waitTime));
      return this.fetchWithAuth(url, options, retryCount + 1);
    }

    return response;
  }

  // ── Provider interface (abstract) ───────────────────────────────────────

  abstract pull(itemPath: string, fileId?: string): Promise<any>;
  abstract push(fileName: string, data: any): Promise<PushResult | void>;
  abstract delete(filePath: string): Promise<void>;
  abstract getMetadata(path: string): Promise<FileMetadata[]>;
  abstract getMetadataAll(projectName: string): Promise<FileMetadata[]>;

  pullById?(fileId: string): Promise<unknown>;
  pushDiff?(remoteEntries: SyncEntry[]): Promise<void>;
  uploadFile?(filePath: string, fileContent: Blob | string | ArrayBuffer): Promise<UploadResult>;
  downloadFile?(filePath: string): Promise<Blob>;

  /**
   * Best-effort, NON-INTERACTIVE verified identity for collaboration
   * rate-limiting. Returns a stable, namespaced id (e.g. "onedrive:<id>") when
   * the user is already authenticated to this provider, or null otherwise — it
   * must never trigger an OAuth popup (so it only acts on an already-stored
   * token and never re-auths). Overridden per provider; providers with no
   * account identity (S3) leave it returning null. See
   * src/collab/identity.ts for the device-id fallback used when this is null.
   */
  getIdentity?(): Promise<string | null>;
}

export default BaseCloudProvider;
